/* eslint-disable react/prop-types */
import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import numeral from 'numeral';
import { withFormContext } from '@glu/validation-react';

import moment from 'moment';
import get from 'lodash/get';
import { maxCharactersTemplate } from '../../gluValidation/utils';
import { lt } from '../../../utils/validations/gluValidations';

import LocaleContext from '../../locale/LocaleContext';
import CurrencyInput from '../../gluValidation/CurrencyInput';
import { getCalculations } from './PaymentInfo';

import WireContext from '../WireContext';

const FormContextWrapper = ({ children, data, validate }) =>
  children({ formData: data, validate });
const FormContext = withFormContext(FormContextWrapper);

const CurrencyWrapper = ({
  action,
  addon,
  balanceCheckType,
  businessDays,
  data,
  formData,
  formState,
  isTemplate,
  locale,
  onParentUpdate,
  setWarningMessage,
  validate,
  warningMessage,
  wireContextState
}) => {
  React.useState(() => {
    if (formData.amount) validate('amount');
  }, [formData.amount]);
  return (
    <CurrencyInput
      {...{
        className: '',
        id: 'amount',
        name: 'amount',
        label: locale['common.amount'],
        maxLength: 13,
        debounceInput: true,
        defaultValue: formState.amount,
        optional: isTemplate,
        onChange: (_, value) => {
          const details = getCalculations({ ...formState, amount: value }, data);
          const { currency, type } = details;
          const templateRelated = action.includes('template');
          if (wireContextState && wireContextState.updateData) {
            wireContextState.updateData({ amount: value });
          }
          onParentUpdate(details[type][templateRelated ? 'debit' : 'sent'], get(currency, '0'));
        },
        validators: [
          {
            fn: (name, _, newValues) => {
              const { amount, amountType, exchangeRate, originatorAccountId, transactionDate } =
                newValues;
              if (
                !amount ||
                !businessDays ||
                (!originatorAccountId && !formData.originatorAccountId)
              )
                return true;
              const balance =
                get(originatorAccountId, '0.balance') ||
                get(formData, 'originatorAccountId.0.balance') ||
                0;
              const sendingForeignCurrency = get(amountType, '0.type') === 'C';

              try {
                const finalAmount = sendingForeignCurrency
                  ? numeral(amount).value() * numeral(exchangeRate).value()
                  : numeral(amount).value();
                // this object is changing after selection, we need additional check
                const date =
                  // eslint-disable-next-line no-nested-ternary
                  typeof transactionDate === 'string'
                    ? moment(transactionDate)
                    : transactionDate &&
                      transactionDate[0] &&
                      typeof transactionDate[0] === 'string'
                    ? moment(transactionDate[0])
                    : transactionDate[0];
                // must account for cut off time
                const actualDate = date.isBefore(moment(businessDays[0]))
                  ? moment(businessDays[0]).startOf('day')
                  : date;
                // if the transaction date is today then run this code as we don't
                // want to run this check on future dated payments
                setWarningMessage(null);
                if (!isTemplate && actualDate && actualDate.isSame(moment(), 'd')) {
                  const invalidAmount = finalAmount > balance;

                  if (invalidAmount && data?.wirePreferences?.balanceCheckType === 0) {
                    return false;
                  }
                  if (invalidAmount && balanceCheckType === 1) {
                    setWarningMessage(locale['payments.wirePayments.balanceCheckWarning']);
                    return true;
                  }
                }
                return true;
              } catch (e) {
                return true;
              }
            },
            msg: locale['wire.insufficientFunds']
          },
          { required: !isTemplate, msg: locale['validation.required'] },
          lt({
            maxLength: 12,
            specialChars: false,
            msg: maxCharactersTemplate(locale, 12)
          })
        ],
        addon,
        warningMessage
      }}
    />
  );
};

const AmountComponent = ({
  action,
  businessDays,
  data,
  formState,
  isTemplate,
  onParentUpdate
}) => {
  const locale = useContext(LocaleContext);
  const wireContextState = useContext(WireContext);
  const balanceCheckType = data?.wirePreferences?.balanceCheckType;
  const [warningMessage, setWarningMessage] = useState();

  return (
    <FormContext>
      {({ formData, validate }) => {
        const { amountTypeCurrency, currencyPrefix } = formData;
        const addon =
          currencyPrefix === 'USD' || amountTypeCurrency === 'D' || !amountTypeCurrency
            ? '$'
            : currencyPrefix;
        return (
          <>
            <CurrencyWrapper
              addon={addon}
              balanceCheckType={balanceCheckType}
              locale={locale}
              formData={formData}
              formState={formState}
              isTemplate={isTemplate}
              data={data}
              action={action}
              wireContextState={wireContextState}
              onParentUpdate={onParentUpdate}
              setWarningMessage={setWarningMessage}
              warningMessage={warningMessage}
              validate={validate}
              businessDays={businessDays}
            />
          </>
        );
      }}
    </FormContext>
  );
};

AmountComponent.defaultProps = {
  onParentUpdate() {},
  isTemplate: false
};

AmountComponent.propTypes = {
  formState: PropTypes.shape({}).isRequired,
  onParentUpdate: PropTypes.func,
  data: PropTypes.shape({}).isRequired,
  action: PropTypes.string.isRequired,
  isTemplate: PropTypes.bool
};

export default AmountComponent;
